import { PLAYER_COLORS } from "@/lib/api";

// Instruction bar + Focus dimming: one pure description of "what this screen should do now".
//
// Result: { key, text, sub, dot, muted, focus, modal, actor } or null.
//   focus  - page regions kept at full colour (others dim): players | board | controls | slot | market | bank | hand | summary | modal
//            (controls = the TURN CONTROLS panel in the right column; modal / summary = the weather panel's event or sea rise).
//            null = nothing dims (other players' turns, "Turn done.").
//   modal  - part of an event (the weather panel) kept bright: wheel (the weather card) | header | choices | response | null (all bright).

export const CONFIRM_TEXT = "Check the summary and confirm. The card is already paid for.";

const nameOf = (game, color) => game.players.find((p) => p.color === color)?.name || "Someone";
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// Player who has to act right now (weather events included).
export function eventActor(game) {
  if (!game) return null;
  // PRIVATE RESPONSE WINDOW (Board Thief block / Mangrove prompt): the player being asked
  // (Pass & Play); online nobody in particular.
  if (game.response_state) return game.response_state.current || null;
  if (game.phase === "weather") {
    // The weather runs by itself: nobody has to act, except a Pass & Play
    // Protection prompt (the player being asked).
    const ws = game.weather_state;
    if (ws?.step === "protection_prompt") return ws.target_color || null;
    return null;
  }
  return game.players[game.current_player_index]?.color || null;
}

// waitingFor: online, the actor's colour when this screen can't act ("Waiting for Asha…").
// Response windows (Protection): never name who holds a card. Online: "your response"
// for a screen still to answer, else "Players are deciding…"; Pass & Play: the neutral cover text.
function responseGuide(make, win, online, seat) {
  const sea = { dot: "var(--sea)", modal: "response" };
  if (online) {
    const mine = !!seat && (win.responders || []).includes(seat) && !(win.answers || {})[seat];
    if (mine) return make(seat, "Choose your response privately before the time runs out.", ["modal"], { modal: "response" });
    return make(null, "Players are deciding on responses…", ["modal"], { ...sea, muted: true });
  }
  return make(null, "Players are deciding on responses, one at a time. Others look away.", ["modal"], sea);
}

export function computeGuidance({ game, seat, online = false, uiPhase, selectedCard, selectedCell, selectedAction = null, acState, demolishMode, seaSummary, turnDone, waitingFor = null, drawing = false }) {
  if (!game) return null;
  const mine = (color) => !seat || seat === color;
  const make = (color, text, focus, extra = {}) => ({
    key: `${color}|${text}|${extra.sub || ""}`,
    text,
    sub: null,
    dot: PLAYER_COLORS[color] || "var(--sea)",
    muted: false,
    focus,
    modal: null,
    actor: color,
    ...extra,
  });
  const other = (color) => make(color, `${nameOf(game, color)} is playing.`, null, { muted: true });
  const active = game.players[game.current_player_index];

  if (game.game_over) return make(null, "The game is over.", null, { muted: true });

  if (seaSummary) {
    return make(null, "The sea rose. Water spread to the low squares it could reach.", ["summary"], { dot: "var(--sea)" });
  }

  if (turnDone && mine(turnDone.color)) return make(turnDone.color, "Turn done.", null);

  if (waitingFor) return make(waitingFor, `Waiting for ${nameOf(game, waitingFor)}…`, null, { muted: true });

  if (game.response_state) {
    return responseGuide(make, game.response_state, online, seat);
  }
  if (game.phase === "weather" && game.weather_state?.step === "protection_prompt") {
    return responseGuide(make, game.weather_state, online, seat);
  }

  if (game.phase === "weather") {
    // The weather runs by itself (no presses): spin overlay → reveal → results → next round.
    const ws = game.weather_state;
    const sea = { dot: "var(--sea)" };
    if (!ws || drawing) return make(null, "End of the round: the weather and global cards are being drawn…", ["modal"], { ...sea, modal: "wheel", key: "weather|spinning" });
    const where =
      ws.kind === "none"
        ? "Nothing happens this round."
        : ws.kind === "surge"
          ? "The sea stands one level higher for this round: it reaches the highlighted squares."
          : `It hits the highlighted shape of ${ws.shape_size || (ws.area || []).length} squares.`;
    return make(null, `${ws.face_name}. ${where}`, ["modal", "board"], { ...sea, sub: "The results are on the left. The next round starts by itself.", modal: "wheel" });
  }

  if (!active) return null;
  const c = active.color;
  if (!mine(c)) return other(c);

  if (game.mode === "setup") {
    const left = active.starting_cubes_remaining ?? 0;
    return make(c, "Place a starting House Cube on an empty square or on your own building.", ["board", "controls"], {
      sub: `${active.name}: ${plural(left, "cube")} left · buildings up to 3 floors.`,
    });
  }

  if (uiPhase === "action") {
    if (demolishMode) return make(c, "Choose one of your squares to demolish.", ["board", "controls"]);
    if (acState) {
      if (acState.step === "confirm") return make(c, CONFIRM_TEXT, ["board", "controls"]);
      if (acState.step === "choose_edge") return make(c, `${acState.card_name}: pick a wall edge on the square.`, ["board"]);
      return make(c, `${acState.card_name}: pick a highlighted square.`, ["board"]);
    }
    if (selectedCard) {
      if (selectedCard.kind === "build") {
        if (selectedCell) return make(c, CONFIRM_TEXT, ["board", "controls"]);
        return make(c, `Place your ${selectedCard.name} on a highlighted square.`, ["board"]);
      }
      return make(c, `Discard your ${selectedCard.name}, or pick another card.`, ["hand", "controls"]);
    }
    if ((active.debt ?? 0) > 0) {
      return make(c, "You're in debt: play cards from your hand (already paid), discard, or end your turn.", ["hand", "controls"], {
        sub: `Debt ${active.debt}: you can't take cards. Round income pays it off first.`,
      });
    }
    if (selectedAction) {
      return make(c, `Press Play under the card to use ${selectedAction.name}, or pick another card.`, ["controls", "players"]);
    }
    const t = game.turn || {};
    const built = t.builds ?? 0;
    return make(c, built > 0 ? "Play another build card, or END TURN." : "Play build cards, take or play an action card, then END TURN.", ["hand", "controls", "bank"], {
      sub: built > 0
        ? `${plural(built, "build card")} played this turn. Your hand cards are paid: playing is free.`
        : "Cards are paid when you take them: playing is free. After each play, take a card (market or blind).",
    });
  }

  if (uiPhase === "draw") {
    const blind = game.blind_card && game.blind_owner === c ? game.blind_card : null;
    if (blind) return make(c, `You drew ${blind.name} blind. Pay ${blind.cost} to keep it.`, ["slot", "controls"], { sub: "Only you see it." });
    const debt = (active.debt ?? 0) > 0;
    const sub = debt
      ? "You're in debt: you can't take cards. Skip the take."
      : "You pay its price now (market) or once you've seen it (blind; can't afford it: discarded). Nothing affordable? Skip.";
    return make(c, "Take a market card or draw blind from the deck.", ["market"], { sub });
  }
  return null;
}
